import type { CSSProperties, FormEvent, ReactNode } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";

import ActionStatusPanel from "../../components/admin/ActionStatusPanel";
import {
  buildCreatePayload,
  CORE_SERVICES,
  PLATFORM_OPTIONS,
  requestAdminAction,
  SLUG_PATTERN,
  TIER_OPTIONS,
  type ActionSummary,
  type AdminTier,
} from "../../lib/admin/adminActions.mjs";
import { getSupabase } from "../../lib/supabase";
import { humanize } from "../../lib/labels";

/**
 * MER-487 PR C: /admin/customers/new. Staff fill in the customer
 * basics, submit, and the page enqueues a customer_create action via
 * the admin_request_action RPC. The droplet poller (PR B) provisions
 * the tenant and finishes by opening a bot-authored config PR; the
 * inline status panel polls the action row until it lands and then
 * surfaces the PR link.
 *
 * Tier offers only the three real templates (MER-447); platform only
 * what the connector layer ingests today (navusoft).
 */

const TIER_LABELS: Record<AdminTier, string> = {
  essential: "Essential",
  growth: "Growth",
  professional: "Pro",
  enterprise: "Enterprise",
};

type SubmitState =
  | { phase: "idle" }
  | { phase: "submitting" }
  | { phase: "requested"; actionId: string }
  | { phase: "error"; message: string };

export default function CustomerCreate() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [tier, setTier] = useState<AdminTier>("growth");
  const [platform, setPlatform] = useState<string>(PLATFORM_OPTIONS[0]);
  const [checkedServices, setCheckedServices] = useState<string[]>([]);
  const [extraServices, setExtraServices] = useState<string[]>([]);
  const [serviceDraft, setServiceDraft] = useState("");
  const [divisions, setDivisions] = useState<string[]>([]);
  const [divisionDraft, setDivisionDraft] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });
  const [lastSummary, setLastSummary] = useState<ActionSummary | null>(null);

  const slugInvalid = slug.length > 0 && !SLUG_PATTERN.test(slug);

  function toggleCoreService(id: string) {
    setCheckedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  }

  function addExtraService() {
    const value = serviceDraft.trim().toLowerCase();
    if (!value) return;
    const coreIds = CORE_SERVICES.map((s) => s.id);
    if (!coreIds.includes(value) && !extraServices.includes(value)) {
      setExtraServices((prev) => [...prev, value]);
    }
    setServiceDraft("");
  }

  function addDivision() {
    const value = divisionDraft.trim();
    if (!value) return;
    if (!divisions.includes(value)) setDivisions((prev) => [...prev, value]);
    setDivisionDraft("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const result = buildCreatePayload({
      name,
      slug,
      tier,
      platform,
      enabled_services: [...checkedServices, ...extraServices],
      divisions,
      access_code: accessCode,
    });
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSubmit({ phase: "submitting" });
    setLastSummary(null);
    try {
      const res = await requestAdminAction(getSupabase(), "customer_create", result.payload);
      setSubmit({ phase: "requested", actionId: res.actionId });
    } catch (err) {
      setSubmit({ phase: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  const actionInFlight =
    submit.phase === "submitting" ||
    (submit.phase === "requested" && !(lastSummary?.terminal ?? false));
  const submitLabel =
    submit.phase === "submitting"
      ? "Submitting..."
      : submit.phase === "requested" && !(lastSummary?.terminal ?? false)
        ? "Provisioning..."
        : lastSummary?.status === "failed"
          ? "Submit again"
          : "Create customer";

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      <header
        style={{
          padding: "22px 28px",
          background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
          border: "1px solid var(--brand-line)",
          borderRadius: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--text-2)",
            fontWeight: 700,
          }}
        >
          Admin / Customers
        </div>
        <h1
          style={{
            margin: "6px 0 0 0",
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: "var(--text)",
          }}
        >
          New customer
        </h1>
        <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)" }}>
          Provisioning runs in the background and finishes with a config pull request. Merge the
          pull request to complete onboarding.
        </div>
      </header>

      <form onSubmit={handleSubmit} style={{ ...panelStyle, maxWidth: 680 }}>
        <Field label="Customer name" error={fieldErrors.name}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Best Disposal"
            style={inputStyle}
          />
        </Field>

        <Field
          label="Slug"
          error={fieldErrors.slug}
          hint={
            slugInvalid
              ? "Slug must be 2-63 characters: lowercase letters, numbers, and hyphens, starting with a letter or number."
              : "Used in the dashboard URL and the upload portal address."
          }
          hintTone={slugInvalid ? "var(--flag-red)" : "var(--text-muted)"}
        >
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="bestdisposal"
            spellCheck={false}
            style={{
              ...inputStyle,
              borderColor: slugInvalid ? "var(--flag-red)" : undefined,
            }}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Tier" error={fieldErrors.tier}>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as AdminTier)}
              style={inputStyle}
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Platform" error={fieldErrors.platform}>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              style={inputStyle}
            >
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {humanize(p)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Enabled services" error={fieldErrors.enabled_services}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {CORE_SERVICES.map((svc) => (
              <label key={svc.id} style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={checkedServices.includes(svc.id)}
                  onChange={() => toggleCoreService(svc.id)}
                />
                <span>{svc.label}</span>
              </label>
            ))}
            {extraServices.map((id) => (
              <label key={id} style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked
                  onChange={() => setExtraServices((prev) => prev.filter((s) => s !== id))}
                />
                <span>{humanize(id)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              type="text"
              value={serviceDraft}
              onChange={(e) => setServiceDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addExtraService();
                }
              }}
              placeholder="Add another service id (e.g. compactor)"
              spellCheck={false}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={addExtraService} style={secondaryButtonStyle}>
              Add
            </button>
          </div>
        </Field>

        <Field label="Divisions" hint="Optional. Add one division at a time.">
          {divisions.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {divisions.map((d) => (
                <span key={d} style={chipStyle}>
                  {d}
                  <button
                    type="button"
                    onClick={() => setDivisions((prev) => prev.filter((x) => x !== d))}
                    aria-label={`Remove division ${d}`}
                    style={chipRemoveStyle}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              value={divisionDraft}
              onChange={(e) => setDivisionDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDivision();
                }
              }}
              placeholder="e.g. main"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={addDivision} style={secondaryButtonStyle}>
              Add
            </button>
          </div>
        </Field>

        <Field
          label="Portal access code"
          hint="Optional. Customers use this code on the upload portal; you can set it later."
        >
          <input
            type="text"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            spellCheck={false}
            style={inputStyle}
          />
        </Field>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="submit"
            disabled={actionInFlight}
            style={actionInFlight ? primaryButtonBusyStyle : primaryButtonStyle}
          >
            {submitLabel}
          </button>
          <Link to="/admin/customers" style={{ fontSize: 13, color: "var(--text-2)" }}>
            Back to customers
          </Link>
        </div>

        {submit.phase === "error" ? (
          <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{submit.message}</div>
        ) : null}

        {submit.phase === "requested" ? (
          <ActionStatusPanel actionId={submit.actionId} onSummary={setLastSummary} />
        ) : null}
      </form>
    </main>
  );
}

function Field(props: {
  label: string;
  error?: string;
  hint?: string;
  hintTone?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <label style={fieldLabelStyle}>{props.label}</label>
      {props.children}
      {props.error ? (
        <div style={{ fontSize: 11, color: "var(--flag-red)" }}>{props.error}</div>
      ) : props.hint ? (
        <div style={{ fontSize: 11, color: props.hintTone ?? "var(--text-muted)" }}>
          {props.hint}
        </div>
      ) : null}
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 16,
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
};

const inputStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "#FFFFFF",
  color: "var(--text)",
};

const checkboxLabelStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "var(--text)",
};

const chipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  padding: "4px 8px",
  borderRadius: 12,
  background: "rgba(38,168,159,0.10)",
  border: "1px solid var(--brand-line)",
  color: "var(--brand-deep)",
};

const chipRemoveStyle: CSSProperties = {
  font: "inherit",
  fontSize: 11,
  lineHeight: 1,
  border: "none",
  background: "transparent",
  color: "var(--brand-deep)",
  cursor: "pointer",
  padding: 0,
};

const secondaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-line)",
  background: "transparent",
  color: "var(--brand-deep)",
  cursor: "pointer",
};

const primaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 700,
  padding: "8px 16px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const primaryButtonBusyStyle: CSSProperties = {
  ...primaryButtonStyle,
  opacity: 0.7,
  cursor: "wait",
};
